import type { JSX } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Sparkles, Copy, Check, RotateCcw, AlertCircle, Bot, Loader2 } from 'lucide-react'
import { useAiStream } from '../../../hooks/useAiStream.js'
import { useToast } from '../../../components/system/ToastProvider.js'

export interface ChatSummaryDialogProps {
  open: boolean
  onClose: () => void
  chatId: string | null
  customerName?: string | null | undefined
}

export function ChatSummaryDialog({
  open,
  onClose,
  chatId,
  customerName,
}: ChatSummaryDialogProps): JSX.Element {
  const { text, running, error, result, run, reset } = useAiStream()
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  const fetchedForRef = useRef<string | null>(null)

  const requestSummary = useCallback(
    (targetId: string) => {
      void run(`/agent/ai/conversations/${targetId}/summarise`, { style: 'brief' })
    },
    [run],
  )

  useEffect(() => {
    if (!open || !chatId) {
      if (!open) {
        fetchedForRef.current = null
        reset()
      }
      return
    }

    if (fetchedForRef.current === chatId) return
    fetchedForRef.current = chatId
    requestSummary(chatId)
  }, [open, chatId, requestSummary, reset])

  const handleCopy = async (): Promise<void> => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      toast.success('Summary copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleRegenerate = (): void => {
    if (!chatId || running) return
    requestSummary(chatId)
  }

  const headerElement = (
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
        <Sparkles className="h-4 w-4" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Chat Summary</h3>
        <p className="text-xs text-slate-500">
          {customerName ? `Conversation with ${customerName}` : 'AI summary in plain, simple words'}
        </p>
      </div>
    </div>
  )

  return (
    <Dialog
      header={headerElement}
      visible={open}
      onHide={onClose}
      style={{ width: '90vw', maxWidth: '34rem' }}
      modal
      closable
      className="p-dialog-custom"
    >
      <div className="space-y-4 pt-2">
        {running && !text && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-indigo-600 mb-3" />
            <p className="text-sm font-medium text-slate-700">Summarising conversation…</p>
            <p className="text-xs text-slate-400 mt-1">Reading messages and creating a simple summary</p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50/60 p-4">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Unable to generate summary</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                >
                  <RotateCcw className="h-3 w-3" />
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {(text || (running && text)) && (
          <div className="space-y-3">
            <div className="relative rounded-lg border border-slate-200 bg-slate-50/80 p-4 text-sm leading-relaxed text-slate-800">
              <p className="whitespace-pre-wrap">{text}</p>
              {running && (
                <span className="inline-block h-3.5 w-1.5 ml-1 animate-pulse bg-indigo-600 align-middle" />
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500 px-1">
              <div className="flex items-center gap-1.5">
                <Bot className="h-3.5 w-3.5 text-indigo-600" />
                <span>{result?.model ?? 'AI Assistant'}</span>
                {typeof result?.latencyMs === 'number' && (
                  <span className="text-slate-400">· {result.latencyMs}ms</span>
                )}
              </div>
              <span className="text-[11px] text-slate-400">Simple paragraph summary</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={!text}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={running || !chatId}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
            >
              <RotateCcw className={`h-3.5 w-3.5 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Regenerating…' : 'Regenerate'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-md bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
